class YakcaWorldDialog extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.dialog = this.querySelector('dialog');
    this.scope = this.closest('.shopify-section') || document;
    this.header = this.scope.querySelector('header-component');
    this.tabs = [...this.querySelectorAll('[data-yk-world-tab]')];
    this.triggers = [...this.scope.querySelectorAll('[data-yk-world-open]')];
    this.desktop = matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');
    this.triggers.forEach(trigger => {
      trigger.addEventListener('pointerenter', event => {
        if (!this.desktop.matches || event.pointerType === 'touch') return;
        this.open(trigger, false);
      }, { signal });
      trigger.addEventListener('click', event => {
        if (this.desktop.matches) return;
        event.preventDefault();
        this.open(trigger, true);
      }, { signal });
      trigger.addEventListener('keydown', event => {
        if (event.key !== 'ArrowDown' && event.key !== ' ') return;
        event.preventDefault();
        this.open(trigger, !this.desktop.matches);
        this.querySelector('[data-yk-world-panel]:not([hidden]) a')?.focus();
      }, { signal });
      trigger.addEventListener('focus', () => {
        if (this.desktop.matches && !this.restoringFocus) this.open(trigger, false);
      }, { signal });
      trigger.addEventListener('pointerleave', () => this.scheduleClose(), { signal });
    });
    this.tabs.forEach(tab => {
      tab.addEventListener('click', () => this.select(tab.dataset.ykWorldTab), { signal });
      tab.addEventListener('pointerenter', () => {
        if (this.desktop.matches) this.select(tab.dataset.ykWorldTab);
      }, { signal });
    });
    this.dialog.addEventListener('pointerenter', () => clearTimeout(this.closeTimer), { signal });
    this.dialog.addEventListener('pointerleave', () => this.scheduleClose(), { signal });
    this.header.addEventListener('pointermove', event => {
      if (this.dialog.open && this.dialog.dataset.mode === 'hover' && !event.target.closest('[data-yk-world-open]')) this.scheduleClose();
    }, { signal });
    this.querySelectorAll('[data-yk-world-close]').forEach(button => button.addEventListener('click', event => this.close(event.detail === 0), { signal }));
    this.dialog.addEventListener('close', () => { if (!this.dialog.open) this.cleanup(); }, { signal });
    this.scope.addEventListener('keydown', event => {
      if (event.key === 'Escape' && this.dialog.open) {
        event.preventDefault();
        this.close(true);
      }
    }, { signal });
    this.dialog.addEventListener('focusout', () => {
      requestAnimationFrame(() => {
        if (document.activeElement !== document.body && this.dialog.open && this.dialog.dataset.mode === 'hover' && !this.dialog.contains(document.activeElement) && !this.triggers.includes(document.activeElement)) this.close();
      });
    }, { signal });
    this.querySelector('[role=tablist]').addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || !this.tabs.includes(event.target)) return;
      event.preventDefault();
      const index = this.tabs.indexOf(document.activeElement);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
      this.select(this.tabs[next].dataset.ykWorldTab);
      this.tabs[next].focus();
    }, { signal });
    window.addEventListener('resize', () => this.close(), { signal });
  }
  open(trigger, modal) {
    clearTimeout(this.closeTimer);
    this.invoker = trigger;
    this.select(trigger.dataset.ykWorldOpen);
    const mode = modal ? 'modal' : 'hover';
    if (this.dialog.open && this.dialog.dataset.mode !== mode) this.close();
    this.dialog.dataset.mode = mode;
    if (!modal) {
      const bottom = Math.max(0, this.header.getBoundingClientRect().bottom);
      this.dialog.style.setProperty('--yk-menu-top', `${bottom}px`);
    }
    if (!this.dialog.open) {
      if (modal) this.dialog.showModal();
      else {
        const previousFocus = document.activeElement;
        this.dialog.show();
        if (document.activeElement !== previousFocus) {
          if (previousFocus === document.body && document.activeElement instanceof HTMLElement) document.activeElement.blur();
          else if (previousFocus instanceof HTMLElement) previousFocus.focus({ preventScroll: true });
        }
      }
    }
    this.header.classList.add('yk-menu-expanded');
    document.documentElement.classList.add('yk-world-open');
    this.updateTriggers();
    if (modal) this.tabs.find(tab => tab.getAttribute('aria-selected') === 'true')?.focus();
  }
  select(world) {
    this.activeWorld = world;
    this.tabs.forEach(tab => {
      const active = tab.dataset.ykWorldTab === world;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    this.querySelectorAll('[data-yk-world-panel]').forEach(panel => panel.hidden = panel.dataset.ykWorldPanel !== world);
    this.querySelector('.yk-world-body').scrollTop = 0;
    this.updateTriggers();
  }
  updateTriggers() {
    this.triggers?.forEach(trigger => {
      const expanded = this.dialog.open && (trigger.dataset.ykWorldOpen === this.activeWorld || trigger.closest('.yk-world-mobile'));
      trigger.setAttribute('aria-expanded', String(Boolean(expanded)));
      trigger.classList.toggle('is-open', Boolean(expanded));
    });
  }
  scheduleClose() {
    if (!this.desktop.matches || this.dialog.dataset.mode !== 'hover') return;
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.close(), 180);
  }
  close(restoreFocus = false) {
    clearTimeout(this.closeTimer);
    if (this.dialog.open) this.dialog.close();
    this.cleanup();
    if (restoreFocus && this.invoker) {
      this.restoringFocus = true;
      this.invoker.focus({ preventScroll: true });
      this.restoringFocus = false;
    }
  }
  cleanup() {
    this.header?.classList.remove('yk-menu-expanded');
    document.documentElement.classList.remove('yk-world-open');
    this.updateTriggers();
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.close();
  }
}
if (!customElements.get('yakca-world-dialog')) customElements.define('yakca-world-dialog', YakcaWorldDialog);
